/**
 * Club role permission keys — what each role can see/do in admin.
 * Platform owner (David) configures the matrix per club; defaults mirror DESIGN.md §19.
 */

import type { ClubRole } from './index';
import { ALL_CLUB_ROLES, COMMITTEE_ROLES, COACH_ROLES } from './clubRoles';

export const PERMISSION_KEYS = [
  'dashboard.view',
  'training.squads.view',
  'training.squads.edit',
  'training.sessions.view',
  'training.programs.view',
  'training.programs.edit',
  'training.statistics.view',
  'people.view',
  'people.edit',
  'people.manage_roles',
  'people.induction.view',
  'people.induction.edit',
  'people.guardians.view',
  'people.guardians.edit',
  'club.website.view',
  'club.website.edit',
  'club.communications.view',
  'club.communications.send',
  'club.store.view',
  'club.store.edit',
  'club.settings.view',
  'club.settings.manage',
  'club.import.view',
  'club.import.edit',
  'club.feedback.view',
  'club.feedback.manage',
  'meets.view',
  'meets.edit',
  'copilot.view',
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const PERMISSION_LABEL: Record<PermissionKey, string> = {
  'dashboard.view': 'Dashboard',
  'training.squads.view': 'Squads — view',
  'training.squads.edit': 'Squads — edit',
  'training.sessions.view': 'Sessions — view',
  'training.programs.view': 'Programs — view',
  'training.programs.edit': 'Programs — edit',
  'training.statistics.view': 'Statistics — view',
  'people.view': 'People — view',
  'people.edit': 'People — edit',
  'people.manage_roles': 'People — assign roles',
  'people.induction.view': 'Induction — view',
  'people.induction.edit': 'Induction — edit',
  'people.guardians.view': 'Guardians — view',
  'people.guardians.edit': 'Guardians — edit',
  'club.website.view': 'Website — view',
  'club.website.edit': 'Website — edit',
  'club.communications.view': 'Communications — view',
  'club.communications.send': 'Communications — send',
  'club.store.view': 'Store — view',
  'club.store.edit': 'Store — edit',
  'club.settings.view': 'Settings — view',
  'club.settings.manage': 'Settings — manage (Xero)',
  'club.import.view': 'Import — view',
  'club.import.edit': 'Import — run',
  'club.feedback.view': 'Feedback — view',
  'club.feedback.manage': 'Feedback — manage',
  'meets.view': 'Meets — view',
  'meets.edit': 'Meets — edit',
  'copilot.view': 'Co-pilot',
};

/** Group labels for the owner permission matrix UI. */
export const PERMISSION_GROUPS: { label: string; keys: PermissionKey[] }[] = [
  {
    label: 'Dashboard',
    keys: ['dashboard.view'],
  },
  {
    label: 'Training',
    keys: [
      'training.squads.view',
      'training.squads.edit',
      'training.sessions.view',
      'training.programs.view',
      'training.programs.edit',
      'training.statistics.view',
    ],
  },
  {
    label: 'People',
    keys: [
      'people.view',
      'people.edit',
      'people.manage_roles',
      'people.induction.view',
      'people.induction.edit',
      'people.guardians.view',
      'people.guardians.edit',
    ],
  },
  {
    label: 'Club',
    keys: [
      'club.website.view',
      'club.website.edit',
      'club.communications.view',
      'club.communications.send',
      'club.store.view',
      'club.store.edit',
      'club.settings.view',
      'club.settings.manage',
      'club.import.view',
      'club.import.edit',
      'club.feedback.view',
      'club.feedback.manage',
    ],
  },
  {
    label: 'Meets & AI',
    keys: ['meets.view', 'meets.edit', 'copilot.view'],
  },
];

/** Roles shown in the admin permission matrix (exclude parent/swimmer — PWA tier). */
export const MATRIX_ROLES: ClubRole[] = [
  ...COMMITTEE_ROLES,
  ...COACH_ROLES,
];

export type RolePermissionMatrix = Record<ClubRole, Record<PermissionKey, boolean>>;

function emptyMatrix(): RolePermissionMatrix {
  const row = Object.fromEntries(PERMISSION_KEYS.map((k) => [k, false])) as Record<
    PermissionKey,
    boolean
  >;
  return Object.fromEntries(ALL_CLUB_ROLES.map((r) => [r, { ...row }])) as RolePermissionMatrix;
}

function grant(m: RolePermissionMatrix, role: ClubRole, ...keys: PermissionKey[]): void {
  for (const k of keys) m[role][k] = true;
}

/**
 * Secretary holds every president capability (union). Secretary-only extras
 * such as communications send and meets edit stay granted.
 */
export function applySecretaryPresidentUnion(matrix: RolePermissionMatrix): void {
  for (const key of PERMISSION_KEYS) {
    if (matrix.president?.[key]) matrix.secretary[key] = true;
  }
}

/**
 * Every committee club_role can view and send login / induction emails
 * (president, secretary, treasurer, membership, marketing, events, and the
 * generic committee member). Re-applied after DB overlay so a stored deny
 * cannot strip this. Coaches stay out. Xero connect and finance follow-up
 * stay treasurer / platform_owner.
 */
export function applyCommitteeLoginEmailGrants(matrix: RolePermissionMatrix): void {
  for (const role of COMMITTEE_ROLES) {
    if (!matrix[role]) continue;
    matrix[role]['people.induction.view'] = true;
    matrix[role]['people.induction.edit'] = true;
  }
}

export function holdsCommitteeLoginEmailRole(held: Iterable<string>): boolean {
  const heldSet = new Set(held);
  return COMMITTEE_ROLES.some((role) => heldSet.has(role));
}

/** `requireRole('president', …)` also admits secretary. Treasurer-only gates stay closed. */
export function expandRequiredRoles(roles: readonly ClubRole[]): ClubRole[] {
  const out = new Set<ClubRole>(roles);
  if (out.has('president')) out.add('secretary');
  return [...out];
}

export function holdsAnyRequiredRole(
  held: Iterable<string>,
  required: readonly ClubRole[],
): boolean {
  const heldSet = new Set(held);
  return expandRequiredRoles(required).some((r) => heldSet.has(r));
}

/** Sensible M1 defaults — president read-mostly; officers scoped per DESIGN.md §19. */
export function buildDefaultPermissionMatrix(): RolePermissionMatrix {
  const m = emptyMatrix();

  const presidentView: PermissionKey[] = [
    'dashboard.view',
    'training.squads.view',
    'training.sessions.view',
    'training.programs.view',
    'training.programs.edit',
    'training.statistics.view',
    'people.view',
    'people.manage_roles',
    'people.induction.view',
    'people.guardians.view',
    'club.website.view',
    'club.communications.view',
    'club.store.view',
    'club.settings.view',
    'club.import.view',
    'club.feedback.view',
    'club.feedback.manage',
    'meets.view',
    'copilot.view',
  ];
  grant(m, 'president', ...presidentView);

  grant(
    m,
    'membership_officer',
    'dashboard.view',
    'people.view',
    'people.edit',
    'people.induction.view',
    'people.induction.edit',
    'people.guardians.view',
    'people.guardians.edit',
    'club.import.view',
    'club.import.edit',
    'club.feedback.view',
    'club.feedback.manage',
    'training.squads.view',
    'training.squads.edit',
    'training.sessions.view',
  );

  grant(
    m,
    'secretary',
    'dashboard.view',
    'people.view',
    'people.manage_roles',
    'club.communications.view',
    'club.communications.send',
    'club.website.view',
    'club.website.edit',
    'club.import.view',
    'club.import.edit',
    'club.feedback.view',
    'club.feedback.manage',
    'meets.view',
    'meets.edit',
    'training.sessions.view',
  );

  grant(m, 'treasurer', 'dashboard.view', 'people.view', 'club.settings.view', 'club.settings.manage');

  grant(
    m,
    'marketing',
    'dashboard.view',
    'club.website.view',
    'club.website.edit',
    'club.communications.view',
    'club.communications.send',
    'club.feedback.view',
    'club.feedback.manage',
  );

  grant(
    m,
    'events_officer',
    'dashboard.view',
    'people.view',
    'meets.view',
    'meets.edit',
    'club.communications.view',
    'club.feedback.view',
    'club.feedback.manage',
  );

  // Intersection of the named committee roles: dashboard, plus the login-email
  // keys re-applied for every COMMITTEE_ROLES entry below. No Xero, comms send,
  // role assignment, people edit, meets, website, or settings.
  grant(m, 'committee_member', 'dashboard.view');

  grant(
    m,
    'head_coach',
    'dashboard.view',
    'training.squads.view',
    'training.squads.edit',
    'training.sessions.view',
    'training.programs.view',
    'training.programs.edit',
    'training.statistics.view',
    'people.view',
    'meets.view',
    'meets.edit',
    'copilot.view',
    'club.feedback.view',
    'club.feedback.manage',
  );

  grant(
    m,
    'squad_coach',
    'dashboard.view',
    'training.squads.view',
    'training.sessions.view',
    'training.programs.view',
    'training.programs.edit',
    'training.statistics.view',
    'copilot.view',
    'club.feedback.view',
  );

  grant(m, 'relief_coach', 'dashboard.view', 'training.squads.view', 'training.sessions.view');

  grant(m, 'junior_coach', 'dashboard.view', 'training.squads.view', 'training.sessions.view');

  grant(m, 'platform_owner', ...PERMISSION_KEYS);
  applyCommitteeLoginEmailGrants(m);
  applySecretaryPresidentUnion(m);

  return m;
}

export const DEFAULT_PERMISSION_MATRIX = buildDefaultPermissionMatrix();

/** Union effective permissions across all held roles. */
export function mergeRolePermissions(
  matrix: RolePermissionMatrix,
  roles: ClubRole[],
): Set<PermissionKey> {
  const out = new Set<PermissionKey>();
  for (const role of roles) {
    const row = matrix[role];
    if (!row) continue;
    for (const key of PERMISSION_KEYS) {
      if (row[key]) out.add(key);
    }
  }
  return out;
}

export function hasPermission(
  effective: Set<PermissionKey>,
  key: PermissionKey,
  isPlatformOwner = false,
): boolean {
  if (isPlatformOwner) return true;
  return effective.has(key);
}

/** Map admin routes to the view permission required. */
export const ROUTE_PERMISSION: Record<string, PermissionKey> = {
  '/app': 'dashboard.view',
  '/app/statistics': 'training.statistics.view',
  '/app/squads': 'training.squads.view',
  '/app/sessions': 'training.sessions.view',
  '/app/programs': 'training.programs.view',
  '/app/people': 'people.view',
  '/app/members': 'people.view',
  '/app/login-emails': 'people.induction.view',
  '/app/induction': 'people.induction.view',
  '/app/users': 'people.manage_roles',
  '/app/guardians': 'people.guardians.view',
  '/app/website': 'club.website.view',
  '/app/communications': 'club.communications.view',
  '/app/store': 'club.store.view',
  '/app/store/orders': 'club.store.view',
  '/app/store/categories': 'club.store.view',
  '/app/finance': 'club.settings.view',
  '/app/finance/donations': 'club.settings.view',
  '/app/settings': 'club.settings.view',
  '/app/settings/role-permissions': 'club.settings.view',
  '/app/import': 'club.import.view',
  '/app/feedback': 'club.feedback.view',
  '/app/update-notes': 'dashboard.view',
  '/app/meets': 'meets.view',
  '/app/calendar': 'club.communications.view',
  '/app/splash': 'copilot.view',
};

export function permissionForPath(pathname: string): PermissionKey | null {
  if (ROUTE_PERMISSION[pathname]) return ROUTE_PERMISSION[pathname];
  for (const [prefix, perm] of Object.entries(ROUTE_PERMISSION)) {
    if (prefix !== '/app' && pathname.startsWith(`${prefix}/`)) return perm;
  }
  return null;
}
